/**
 * useAccountTemplate.ts
 */
import Focus from '@shared/types/enums/focus';
import useBackDoorCombination from '../../../../utils/hooks/useBackDoorCombination';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo } from 'react';

/**
 * Hook to use the account template.
 * Handles the account template logic.
 */
export default function useAccountTemplate() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const focus = useAppSelector(selectFocus);

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.ACCOUNT));
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.ACCOUNT));
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Displays hidden endpoint page
  useBackDoorCombination();

  // Account keyboard
  useKeyboard({
    focus: Focus.ACCOUNT,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'BACK':
          dispatch(popRoute());
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
      }
    },
  });

  // Menu keyboard
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.ACCOUNT));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      callbacks: {
        onMouseEnter,
      },
      props: {
        animation:
          focus === Focus.MENU ? 'main-content-out' : 'main-content-in',
        force: true,
        id: 'accountContainer',
      },
    };
  }, [focus]);
}
